import React, { createContext, useContext, useState, ReactNode } from 'react';
import { qryCopilotDialogApi } from '@/api/copilot';
import { EXTERNAL_USER_ID } from '@/config/env';
import useUserStore from '@/stores/userStore';
// 对话类型映射
export const DIALOG_TYPE_MAP = {
  advisory: 0, // 投顾文案改写/未选择技能下对话
  recommendation: 1, // 产品推荐对话
  compare: 1, // 产品对比对话
  interpretation: 1, // 产品解读对话
  market: 2 // 市场对比对话
} as const;

export type positionMessageType = 'left' | 'right';

// 对话类型描述
export const DIALOG_TYPE_DESCRIPTIONS = {
  0: '投顾文案改写/未选择技能下对话',
  1: '产品推荐/产品对比/产品解读对话',
  2: '市场解读对话'
} as const;

interface ChatContextType {
  messages: { position: positionMessageType; content: string; loading?: boolean }[];
  selectedButton: string | null;
  isProcessing: boolean;
  addMessage: ({
    position,
    content,
    loading
  }: {
    position: positionMessageType;
    content: string;
    loading?: boolean;
  }) => void;
  setSelectedButton: (button: string | null) => void;
  setProcessing: (processing: boolean) => void;
  processMessage: (message: string, buttonType: string | null) => Promise<void>;
  getDialogType: (buttonType: string | null) => number;
  getDialogDescription: (buttonType: string | null) => string;
  clearMessages: () => void;
  isLoading: boolean;
  clearLoading: () => void;
  // setChatId: (chatId: string | null) => void;
  // chartType: 'advisory' | 'market' | 'interpretation' | 'compare';
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
  chartsViewportRef: React.RefObject<HTMLDivElement>;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children, chartsViewportRef }) => {
  const [messages, setMessages] = useState<
    { position: positionMessageType; content: string; loading?: boolean }[]
  >([
    {
      position: 'left',
      content:
        '您好，我是您的AI智能投顾助手。我可以帮您分析市场行情、推荐投资产品、制定资产配置方案，以及回答各类投资理财问题。您可以通过底部输入框与我交流。',
      loading: false
    }
  ]);
  const [selectedButton, setSelectedButton] = useState<string | null>('advisory');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const { chatId, updateChatId } = useUserStore();

  const clearLoading = () => {
    const _messages = [...messages];
    _messages.forEach(msg => {
      if (msg.loading) {
        msg.loading = false;
      }
    });
    console.log(_messages, 'clearLoading');
    setMessages(_messages);
    setIsLoading(false);
    setProcessing(false);
  };

  const addMessage = (message: {
    position: positionMessageType;
    content: string;
    loading?: boolean;
  }) => {
    setMessages(prev => {
      // 如果新消息有 loading 状态，先添加 loading 消息
      if (message.loading) {
        return [...prev, message];
      }

      // 如果新消息没有 loading 状态，查找并替换最后一个 loading 消息
      const findLoadingMessage = prev.findIndex(msg => msg.loading);

      if (findLoadingMessage >= 0) {
        const newMessages = [...prev];
        newMessages[findLoadingMessage] = message;
        return newMessages;
      } else {
        // 如果没有找到 loading 消息，直接添加
        return [...prev, message];
      }
    });
  };

  // 获取对话类型
  const getDialogType = (buttonType: string | null): number => {
    if (buttonType === null) {
      return 0;
    }
    console.log(buttonType, 'buttonType');

    return DIALOG_TYPE_MAP[buttonType as keyof typeof DIALOG_TYPE_MAP] ?? null;
  };

  // 获取对话类型描述
  const getDialogDescription = (buttonType: string | null): string => {
    const dialogType = getDialogType(buttonType);
    return (
      DIALOG_TYPE_DESCRIPTIONS[dialogType as keyof typeof DIALOG_TYPE_DESCRIPTIONS] ??
      DIALOG_TYPE_DESCRIPTIONS[0]
    );
  };

  const processMessage = async (message: string, buttonType: string | null) => {
    console.log(message, buttonType, 'processMessage');
    setIsLoading(true);
    setProcessing(true);
    try {
      let reqType = getDialogType(buttonType);
      if (reqType === null || reqType === undefined) {
        reqType = 0;
      }

      // 这里可以调用实际的接口
      const response = await qryCopilotDialogApi({
        chatId,
        text: message,
        reqType,
        externalUserId: EXTERNAL_USER_ID
      });

      const result = response.data;
      if (result.code === '000000') {
        updateChatId(result.data?.chatId);
        // 接口成功后添加消息
        addMessage({ position: 'left', content: result.data?.data?.content });
        setTimeout(() => {
          chartsViewportRef.current?.scrollTo({
            top: chartsViewportRef.current?.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      } else {
        addMessage({ position: 'left', content: null });
      }
    } catch (error) {
      console.error('接口调用失败:', error);
      clearLoading();
    } finally {
      setProcessing(false);
      setIsLoading(false);
      // const _messages = [...messages];
      // _messages.forEach(msg => {
      //   if (msg.loading) {
      //     msg.loading = false;
      //   }
      // });
      // setMessages(_messages);
    }
  };

  const clearMessages = () => {
    setMessages([
      {
        position: 'left',
        content:
          '您好，我是您的AI智能投顾助手。我可以帮您分析市场行情、推荐投资产品、制定资产配置方案，以及回答各类投资理财问题。您可以通过底部输入框与我交流。',
        loading: false
      }
    ]);
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        selectedButton,
        isProcessing,
        addMessage,
        setSelectedButton,
        setProcessing,
        processMessage,
        getDialogType,
        getDialogDescription,
        clearMessages,
        isLoading,
        clearLoading
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
